import { useSlideEditorContext } from "@/components/slides/editor/SlideEditorContext";
import { useAddQuestionElement } from "@/lib/hooks/slides/editor/useAddQuestionELement";
import { type LineConfig, useAddShapeElement } from "@/lib/hooks/slides/editor/useAddShapeElement";
import { useAddTextElement } from "@/lib/hooks/slides/editor/useAddTextElement";
import { useMediaHandler } from "@/lib/hooks/slides/editor/useMediaHandler";
import { useTextStyle } from "@/lib/hooks/slides/editor/useTextStyle";
import { useAppDispatch } from "@/store/hooks";
import { setSelectedId, updateElement } from "@/store/slideEditorSlice";
import { store } from "@/store/store";
import type { ShapeType, TextStyle } from "@ai-teach/types";
import {
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  PointerSensor,
  closestCenter,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useState } from "react";
import { DndOverlay } from "../components/DndOverlay";
import type { DragData, DropData } from "../types";

export function useDnd() {
  const [draggedItem, setDraggedItem] = useState<DragData | null>(null);
  const { addAudioToSlide, addImageToSlide, addVideoToSlide } = useMediaHandler();
  const { handleAddShapeItem, handleAddLineItem } = useAddShapeElement();
  const { handleAddText } = useAddTextElement();
  const { handleAddQuestionElement } = useAddQuestionElement();
  const { handleApplyTextStyle } = useTextStyle();
  const { setActivePanel, canvasRef } = useSlideEditorContext();
  const dispatch = useAppDispatch();

  // Cursor position at drop (activator + delta, not the overlay rect), in unscaled canvas px.
  const getDropCenter = (event: DragEndEvent): { x: number; y: number } | null => {
    try {
      const rect = canvasRef.current?.getBoundingClientRect();
      const activator = event.activatorEvent as PointerEvent | undefined;
      if (!rect || !rect.width || !rect.height || activator?.clientX == null) return null;
      const px = activator.clientX + event.delta.x;
      const py = activator.clientY + event.delta.y;
      if (px < rect.left || px > rect.right || py < rect.top || py > rect.bottom) return null;
      const { canvasSize } = store.getState().slideEditor.present;
      const unscaledW = canvasSize.height * canvasSize.aspectRatio;
      const unscaledH = canvasSize.height;
      const x = ((px - rect.left) / rect.width) * unscaledW;
      const y = ((py - rect.top) / rect.height) * unscaledH;
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      return { x, y };
    } catch {
      return null;
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const dragData = event.active.data.current as DragData | undefined;
    // Sortable cards share this context and carry no media type; they are not panel items, so
    // they must not close the panel, clear the selection or raise a media overlay.
    if (!dragData?.type) return;
    setDraggedItem(dragData);

    // Close the panel; deselect so a text-style drop always adds a new element.
    setActivePanel(null);
    dispatch(setSelectedId(null));
  };

  const handleDragAbort = () => {
    setDraggedItem(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const dropData = event.over?.data.current as DropData;

    // Only process drop if valid drop zone and dragged item
    if (draggedItem?.id && dropData?.id) {
      const dropCenter = getDropCenter(event) ?? undefined;
      switch (draggedItem.type) {
        case "image":
          await addImageToSlide(draggedItem.id, dropData.id, dropCenter);
          break;
        case "audio":
          await addAudioToSlide(draggedItem.id, dropData.id, undefined, dropCenter);
          break;
        case "video":
          await addVideoToSlide(draggedItem.id, dropData.id, dropCenter);
          break;
        case "shape":
          handleAddShapeItem(draggedItem.id as ShapeType);
          break;
        case "text":
          handleAddText(draggedItem.id);
          break;
        case "line":
          handleAddLineItem(draggedItem.data as LineConfig);
          break;
        case "question":
          handleAddQuestionElement(draggedItem.id);
          break;
        case "textStyle":
          handleApplyTextStyle(draggedItem.data as TextStyle);
          break;
        default:
          console.warn(`Unsupported media type: ${draggedItem.type}`);
      }

      // Sync adds select the new element — center it on the drop point (media self-positions).
      try {
        if (["shape", "text", "line", "question", "textStyle"].includes(draggedItem.type)) {
          const center = dropCenter;
          const state = store.getState().slideEditor.present;
          const el = state.slide?.elements.find((e) => e.id === state.selectedId);
          if (center && el) {
            const w = typeof el.size?.width === "number" ? el.size.width : 0;
            const h = typeof el.size?.height === "number" ? el.size.height : 0;
            const unscaledW = state.canvasSize.height * state.canvasSize.aspectRatio;
            dispatch(
              updateElement({
                id: el.id,
                position: {
                  x: Math.max(0, Math.min(center.x - w / 2, unscaledW - w)),
                  y: Math.max(0, Math.min(center.y - h / 2, state.canvasSize.height - h)),
                },
              })
            );
          }
        }
      } catch {
        // keep the element at its default position rather than breaking the drop
      }
    }

    // Always clear draggedItem, even if drop was invalid
    setDraggedItem(null);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  /**
   * Media drops and card reordering share this context, and a card's rect covers every media zone
   * nested inside it. Matching each drag against only its own kind of droppable keeps a dragged
   * clip from resolving to the card and a dragged card from resolving to one of its own zones.
   */
  const collisionDetection: CollisionDetection = (args) => {
    const isSortableDrag = !!args.active.data.current?.sortable;
    const droppableContainers = args.droppableContainers.filter(
      (container) => !!container.data.current?.sortable === isSortableDrag
    );

    return isSortableDrag
      ? closestCenter({ ...args, droppableContainers })
      : rectIntersection({ ...args, droppableContainers });
  };

  const Overlay = <DndOverlay draggedItem={draggedItem} />;

  return {
    handleDragEnd,
    handleDragStart,
    handleDragAbort,

    Overlay,
    sensors,
    collisionDetection,
    draggedItem,
  };
}
